// Step-by-step answer panel for the five Level 1 tickets (replaces the eight free-text fields).
// Checking is done by src/core/guidedTicket.js; this file only renders and collects input.
import { h } from './h.js';
import { play } from './sound.js';
import { checkGuidedStep, firstOpenStep, outputsFor, noteText, noteTemplate, noteHeadings, noteChecklist, exampleNote, stepCommands } from '../core/guidedTicket.js';

const labelOf = (pairs, id) => (pairs || []).find(([k]) => k === id)?.[1] ?? id;

/** Human-readable form of a step value (admin expected answer, review list). */
export function guidedValueLabel(step, value) {
  if (value == null) return '—';
  switch (step.kind) {
    case 'choice': return labelOf(step.options, value);
    case 'multi': return value.map((v) => labelOf(step.options, v)).join(' · ');
    case 'order': return value.map((v, i) => `${i + 1}. ${labelOf(step.items, v)}`).join('  ');
    case 'evidence': return value;
    case 'note': return noteText(step, value) || '—';
    default: return String(value);
  }
}

/**
 * @param {object} o
 * @param {object} o.ticket  ticket with `guided` steps
 * @param {object} o.track   support progress API
 * @param {object} o.ui      per-ticket UI state kept by the caller across redraws
 * @param {Function} o.redraw
 * @param {Function} o.terminal  (commands: string[]) => terminal element (runs on the simulated endpoint)
 * @param {Function} o.onCompleteTicket  () => void, called by the final button
 * @param {boolean} o.locked  ticket complete (read-only for learners)
 */
export function guidedPanel({ ticket, track, ui, redraw, terminal, onCompleteTicket, locked = false }) {
  const steps = ticket.guided;
  const rec = track.ticketRecord(ticket.id);
  const saved = rec.guided || {};
  ui.draft ||= {}; ui.feedback ||= {};
  const open = firstOpenStep(ticket, rec);
  const allDone = open >= steps.length;
  let index = ui.view ?? open;
  if (index > open) index = open; // never skip ahead of an unanswered step
  const showFinal = index >= steps.length;
  const step = showFinal ? null : steps[index];

  const progress = h('p', { class: 'rz-practice-progress', 'data-testid': 'guided-progress', 'aria-live': 'polite' },
    locked ? 'Ticket complete' : showFinal ? 'All steps done' : `Step ${index + 1} of ${steps.length}`);
  const nav = h('ol', { class: 'rz-guided-nav', 'aria-label': 'Ticket steps' }, steps.map((s, i) => {
    const done = i < open;
    const current = i === index;
    const label = h('span', {}, s.title);
    const mark = h('span', { class: 'rz-guided-dot', 'aria-hidden': 'true' }, done ? '✓' : String(i + 1));
    return h('li', { class: `${done ? 'done' : ''} ${current ? 'current' : ''}` },
      done || current
        ? h('button', { type: 'button', 'data-testid': `guided-nav-${s.id}`, 'aria-current': current ? 'step' : null, onclick: () => { ui.view = i; redraw(); } }, mark, label)
        : h('span', { class: 'rz-guided-locked' }, mark, label));
  }));

  if (locked || showFinal) {
    const summary = h('ul', { class: 'rz-guided-review' }, steps.map((s) =>
      h('li', {}, h('b', {}, s.title), h('span', {}, guidedValueLabel(s, saved[s.id])))));
    return h('section', { class: 'rz-workflow panel rz-guided', 'data-testid': 'guided-panel' },
      progress, nav, h('div', { class: 'eyebrow' }, 'YOUR ANSWERS'), summary,
      locked ? null : allDone ? h('button', { class: 'btn primary', type: 'button', 'data-testid': 'support-complete-ticket', onclick: onCompleteTicket }, 'Complete ticket') : null);
  }

  const current = () => (Object.hasOwn(ui.draft, step.id) ? ui.draft[step.id] : saved[step.id]);
  const setDraft = (v, again = false) => { ui.draft[step.id] = v; delete ui.feedback[step.id]; if (again) redraw(); };
  const value = current();
  const body = [];

  // on evidence steps the output is shown once, as clickable lines, so the terminal stays compact
  if (stepCommands(step).length) body.push(terminal(stepCommands(step), { compact: step.kind === 'evidence' && outputsFor(rec, step.pick.command).length > 0 }));

  if (step.kind === 'choice' || step.kind === 'multi') {
    const multi = step.kind === 'multi';
    const picked = multi ? (Array.isArray(value) ? value : []) : value;
    body.push(h('fieldset', { class: `rz-guided-options ${step.display === 'cards' ? 'cards' : ''}`, 'data-testid': 'guided-options' },
      h('legend', {}, step.prompt), multi ? h('small', { class: 'muted' }, 'Select all that apply.') : null,
      ...step.options.map(([id, label]) => h('label', { class: 'rz-practice-choice' },
        h('input', {
          type: multi ? 'checkbox' : 'radio', name: `guided-${ticket.id}-${step.id}`, value: id, 'data-testid': `guided-option-${id}`,
          checked: multi ? picked.includes(id) : picked === id,
          onchange: (e) => {
            if (!multi) return setDraft(id);
            const now = new Set(Array.isArray(current()) ? current() : []);
            e.target.checked ? now.add(id) : now.delete(id);
            setDraft(step.options.map(([k]) => k).filter((k) => now.has(k)));
          },
        }), h('span', {}, label)))));
  } else if (step.kind === 'order') {
    const order = Array.isArray(value) && value.length === step.items.length ? value : step.items.map(([id]) => id);
    const move = (from, to) => {
      if (to < 0 || to >= order.length || from === to) return;
      const next = [...order]; const [x] = next.splice(from, 1); next.splice(to, 0, x); setDraft(next, true);
    };
    body.push(h('div', { class: 'rz-guided-options' },
      h('p', { class: 'rz-guided-prompt' }, step.prompt),
      h('small', { class: 'muted' }, 'Drag the cards, or use the arrow buttons.'),
      h('ol', { class: 'rz-guided-order', 'data-testid': 'guided-order' }, order.map((id, i) => h('li', {
        draggable: 'true', 'data-id': id,
        ondragstart: (e) => { e.dataTransfer.setData('text/plain', String(i)); e.dataTransfer.effectAllowed = 'move'; },
        ondragover: (e) => e.preventDefault(),
        ondrop: (e) => { e.preventDefault(); move(Number(e.dataTransfer.getData('text/plain')), i); },
      },
      h('span', { class: 'rz-guided-dot', 'aria-hidden': 'true' }, String(i + 1)),
      h('span', { class: 'rz-guided-order-label' }, labelOf(step.items, id)),
      h('span', { class: 'rz-guided-order-moves' },
        h('button', { type: 'button', class: 'btn small ghost', 'data-testid': `guided-up-${id}`, 'aria-label': `Move “${labelOf(step.items, id)}” up`, disabled: i === 0 ? true : null, onclick: () => move(i, i - 1) }, '↑'),
        h('button', { type: 'button', class: 'btn small ghost', 'data-testid': `guided-down-${id}`, 'aria-label': `Move “${labelOf(step.items, id)}” down`, disabled: i === order.length - 1 ? true : null, onclick: () => move(i, i + 1) }, '↓')))))));
  } else if (step.kind === 'evidence') {
    const outs = outputsFor(rec, step.pick.command);
    const lines = outs.length ? outs.at(-1).split('\n').map((l) => l.replace(/\s+$/, '')).filter((l) => l.trim()) : [];
    body.push(h('div', { class: 'rz-guided-options' },
      h('p', { class: 'rz-guided-prompt' }, step.prompt),
      lines.length
        ? h('div', { class: 'rz-guided-lines', role: 'group', 'aria-label': `Output of ${step.pick.command} — choose the line that proves it`, 'data-testid': 'guided-lines' },
          lines.map((line, i) => {
            const on = String(value || '') === line.replace(/\s+/g, ' ').trim();
            return h('button', { type: 'button', class: on ? 'on' : '', 'aria-pressed': on ? 'true' : 'false', 'data-testid': `guided-line-${i}`, onclick: () => setDraft(line, true) }, line);
          }))
        : h('p', { class: 'muted small', 'data-testid': 'guided-run-first' }, `Run ${step.pick.command} above — its output will appear here so you can pick the line.`)));
  } else if (step.kind === 'note') {
    // Any note is accepted — it is the learner's own record of the ticket. Older saves made with the
    // dropdown builder are shown as text so nothing is lost.
    const v = value && typeof value === 'object' ? value : { parts: {}, text: '' };
    const update = (text, again) => setDraft({ parts: {}, text }, again);
    const area = h('textarea', { id: `guided-text-${ticket.id}`, rows: noteHeadings(step).length > 5 ? 9 : 6, 'data-testid': 'guided-text', oninput: (e) => update(e.target.value) }, noteText(step, v));
    body.push(h('div', { class: 'rz-guided-options' },
      h('label', { class: 'rz-ticket-field', for: `guided-text-${ticket.id}` },
        h('span', { class: 'rz-kicker' }, 'YOUR NOTE'), h('small', {}, step.prompt), area),
      h('p', { class: 'rz-guided-note-tip muted small', 'data-testid': 'guided-note-tip' },
        `Any note is accepted. A strong note usually has a line for: ${noteHeadings(step).join(' · ')}.`),
      h('button', { type: 'button', class: 'btn small ghost', 'data-testid': 'guided-template', onclick: () => { const t = (current() ? noteText(step, current()) : '').trim(); update(t ? `${t}\n${noteTemplate(step)}` : noteTemplate(step), true); } }, 'Insert headings')));
  }

  const fb = ui.feedback[step.id];
  const check = () => {
    const v = current();
    track.saveGuided(ticket.id, step.id, v);
    const r = checkGuidedStep(step, v, track.ticketRecord(ticket.id));
    play(r.ok ? 'chime' : 'wrong');
    ui.feedback[step.id] = step.kind === 'note'
      ? (r.ok ? { ok: true, note: true, text: `Note saved. ${step.explain}` } : { ok: false, text: r.reason })
      : r.ok ? { ok: true, text: `Correct. ${step.explain}` } : { ok: false, text: r.reason ? `${r.reason} Hint: ${step.hint}` : `Not quite. Hint: ${step.hint}` };
    if (r.ok) delete ui.draft[step.id];
    ui.view = index; // stay on this step so the feedback can be read
    redraw();
  };
  const last = index === steps.length - 1;
  const passed = checkGuidedStep(step, saved[step.id], rec).ok;
  return h('section', { class: 'rz-workflow panel rz-guided', 'data-testid': 'guided-panel' },
    progress, nav,
    h('div', { class: 'rz-guided-step', 'data-testid': `guided-step-${step.id}`, 'data-kind': step.kind },
      h('div', { class: 'eyebrow' }, `STEP ${index + 1} · ${step.title.toUpperCase()}`),
      ...body,
      h('div', { class: 'row rz-guided-actions' },
        h('button', { class: 'btn primary', type: 'button', 'data-testid': 'guided-check', onclick: check }, step.kind === 'note' ? 'Save note' : 'Check answer'),
        (fb?.ok || (passed && !fb)) ? (last
          ? h('button', { class: 'btn primary', type: 'button', 'data-testid': 'support-complete-ticket', onclick: onCompleteTicket }, 'Complete ticket')
          : h('button', { class: 'btn ghost', type: 'button', 'data-testid': 'guided-continue', onclick: () => { delete ui.feedback[step.id]; ui.view = null; redraw(); } }, 'Continue →')) : null),
      fb ? h('div', { class: `rz-practice-feedback ${fb.ok ? 'ok' : 'bad'}`, role: 'status', 'data-testid': 'guided-feedback' }, fb.text) : null,
      fb?.note ? noteReview(step, noteText(step, saved[step.id])) : null));
}

/** After a note is saved: a non-blocking checklist and an example note to compare with. */
function noteReview(step, text) {
  const list = noteChecklist(step, text);
  const have = list.filter((x) => x.ok).length;
  return h('div', { class: 'rz-guided-note-review', 'data-testid': 'guided-note-review' },
    h('div', { class: 'rz-kicker' }, `YOUR NOTE HAS ${have} OF ${list.length} USUAL LINES`),
    h('ul', { class: 'rz-guided-note-check', 'data-testid': 'guided-note-checklist' }, list.map((x) =>
      h('li', { class: x.ok ? 'ok' : '', 'data-ok': x.ok ? 'true' : 'false' }, h('span', { 'aria-hidden': 'true' }, x.ok ? '✓' : '○'), h('span', { class: 'sr-only' }, x.ok ? 'Has: ' : 'Could add: '), x.label))),
    h('div', { class: 'rz-kicker' }, 'AN EXAMPLE NOTE TO COMPARE'),
    h('pre', { class: 'rz-guided-preview', 'data-testid': 'guided-note-example' }, exampleNote(step)),
    h('p', { class: 'muted small' }, 'This is optional feedback — your own note is already saved and accepted.'));
}
